import { Badge as AntBadge, BadgeProps as AntBadgeProps, theme } from 'antd';
import styled from 'styled-components';
import type { GlobalToken } from 'antd';
import { radius } from '../../theme/radius';

export type BadgeStatus = 'default' | 'success' | 'warning' | 'error' | 'info';

export interface BadgeProps extends Omit<AntBadgeProps, 'status'> {
  /** Badge status color */
  status?: BadgeStatus;
  /** Number to display */
  count?: number;
  /** Show dot instead of count */
  dot?: boolean;
  /**
   * Pulse the dot. Implemented via antd's own `processing`
   * status animation, recolored to match `status` — antd only animates
   * `processing`, never `success`.
   */
  animated?: boolean;
  /** Whether to show badge when count is zero */
  showZero?: boolean;
  /** Maximum count to display (shows {max}+ when exceeded) */
  overflowCount?: number;
  /** Content to wrap with badge */
  children?: React.ReactNode;
}

const getStatusColor = (token: GlobalToken, status: BadgeStatus): string => {
  const statusColors: Record<BadgeStatus, string> = {
    default: token.colorTextDisabled,
    success: token.colorSuccess,
    warning: token.colorWarning,
    error: token.colorError,
    info: token.colorInfo,
  };
  return statusColors[status];
};

interface StyledBadgeProps {
  $token: GlobalToken;
  $status: BadgeStatus;
}

const StyledBadge = styled(AntBadge)<StyledBadgeProps>`
  .ant-badge-count {
    font-family: ${({ $token }) => $token.fontFamily};
    font-size: ${({ $token }) => $token.fontSizeSM - 2}px;
    font-weight: ${({ $token }) => $token.fontWeightStrong};
    background: ${({ $token, $status }) => getStatusColor($token, $status)};
    box-shadow: 0 0 0 ${({ $token }) => $token.lineWidthBold}px
      ${({ $token }) => $token.colorBgContainer};
    min-width: 18px;
    height: 18px;
    line-height: 18px;
    border-radius: ${radius.pill}px;
    padding: 0 ${({ $token }) => $token.paddingXXS + 1}px;
  }

  .ant-badge-dot {
    width: ${({ $token }) => $token.marginXS}px;
    height: ${({ $token }) => $token.marginXS}px;
    background: ${({ $token, $status }) => getStatusColor($token, $status)};
    box-shadow: 0 0 0 ${({ $token }) => $token.lineWidthBold}px
      ${({ $token }) => $token.colorBgContainer};
  }

  .ant-badge-status-dot {
    width: ${({ $token }) => $token.marginXS}px;
    height: ${({ $token }) => $token.marginXS}px;
    background: ${({ $token, $status }) => getStatusColor($token, $status)};
  }

  .ant-badge-status-text {
    font-family: ${({ $token }) => $token.fontFamily};
    font-size: ${({ $token }) => $token.fontSizeSM}px;
    color: ${({ $token }) => $token.colorText};
    margin-left: ${({ $token }) => $token.marginXS}px;
  }
`;

interface StandaloneBadgeProps {
  $token: GlobalToken;
  $status: BadgeStatus;
}

const StandaloneBadge = styled.span<StandaloneBadgeProps>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-family: ${({ $token }) => $token.fontFamily};
  font-size: ${({ $token }) => $token.fontSizeSM - 2}px;
  font-weight: ${({ $token }) => $token.fontWeightStrong};
  background: ${({ $token, $status }) => getStatusColor($token, $status)};
  color: ${({ $token }) => $token.colorWhite};
  min-width: 18px;
  height: 18px;
  line-height: 18px;
  border-radius: ${radius.pill}px;
  padding: 0 ${({ $token }) => $token.paddingXXS + 1}px;
`;

const StandaloneDot = styled.span<StandaloneBadgeProps>`
  display: inline-block;
  width: ${({ $token }) => $token.marginXS}px;
  height: ${({ $token }) => $token.marginXS}px;
  border-radius: ${radius.circle};
  background: ${({ $token, $status }) => getStatusColor($token, $status)};
`;

/**
 * Badge component based on Libertify Design System
 *
 * Now uses Ant Design theme tokens for consistent styling.
 *
 * @example
 * ```tsx
 * import { Badge } from '@libertify/ui';
 *
 * // Count badge
 * <Badge count={5}>
 *   <NotificationIcon />
 * </Badge>
 *
 * // Dot badge
 * <Badge dot status="success">
 *   <Avatar />
 * </Badge>
 *
 * // Animated dot (e.g. unread indicator)
 * <Badge dot status="success" animated />
 *
 * // Standalone badge
 * <Badge count={99} overflowCount={99} />
 * ```
 */
export const Badge = ({
  status = 'error',
  count,
  dot = false,
  animated = false,
  showZero = false,
  overflowCount = 99,
  children,
  color,
  ...props
}: BadgeProps) => {
  const { token } = theme.useToken();

  // Standalone badge (no children)
  if (!children && !dot && count !== undefined) {
    const displayCount =
      count > overflowCount ? `${overflowCount}+` : count.toString();
    if (count === 0 && !showZero) return null;
    return (
      <StandaloneBadge $token={token} $status={status}>
        {displayCount}
      </StandaloneBadge>
    );
  }

  // Standalone dot
  if (!children && dot) {
    if (animated) {
      return (
        <StyledBadge
          $token={token}
          $status={status}
          status="processing"
          color={color ?? getStatusColor(token, status)}
        />
      );
    }
    return <StandaloneDot $token={token} $status={status} />;
  }

  // Badge wrapping children
  return (
    <StyledBadge
      $token={token}
      $status={status}
      count={dot ? undefined : count}
      dot={dot}
      showZero={showZero}
      overflowCount={overflowCount}
      color={color}
      {...props}
    >
      {children}
    </StyledBadge>
  );
};

export default Badge;
